/**
 * ログインしていない人の検討。 ブラウザの中だけで完結し、 Worker も Durable Object も
 * 使わない。 操作の意味は `mixed-do::study` と同じにする。 解析結果は読み直せるので
 * 保存しない。
 */
import { ROOT, type BoardMeta, type BoardTree, type Cursor, type PositionAnalysis, type StudyMeta, type StudyNode } from './tree';

/** `l` はサーバの検討 id に使わない文字。 */
const PREFIX = 'l';
const ID_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
const STORAGE_PREFIX = 'mixed-study:';
const HIDDEN_INFO_GAMES = ['HeadsUpPoker', 'BridgeNT'];
const MAX_STORED = 20;
export const LOCAL_SUB = 'local';

export type LocalOp =
  | { t: 'add'; board: number; parent: string; mv: unknown }
  | { t: 'memo'; board: number; node: string; text: string }
  | { t: 'delete'; board: number; node: string }
  | { t: 'cursor'; board: number | null; node: string }
  | { t: 'analysis'; board: number; node: string; depth: number; data: PositionAnalysis };

export interface NewBoard extends BoardMeta {
  moves: unknown[];
}

export interface LocalBody {
  title: string;
  host_seat: string | null;
  outcome: string | null;
  players: string[];
  boards: unknown[];
  creator_seat: string | null;
}

export interface LocalDoc {
  meta: StudyMeta;
  trees: Record<string, BoardTree>;
  cursor: Cursor;
}

export function isLocalStudy(id: string): boolean {
  return id.startsWith(PREFIX);
}

function mainNodes(moves: unknown[]): Record<string, StudyNode> {
  const nodes: Record<string, StudyNode> = {};
  let parent = ROOT;
  moves.forEach((mv, i) => {
    const id = `n${i}`;
    nodes[id] = { id, parent, mv, main: true, memo: '', deleted: false, by: '', seq: i };
    parent = id;
  });
  return nodes;
}

export function buildDoc(body: LocalBody): LocalDoc {
  const boards = (body.boards as NewBoard[]).filter((b) => !HIDDEN_INFO_GAMES.includes(b.game_id));
  if (boards.length === 0) throw new Error('検討できる盤がありません');
  const { boards: _, ...rest } = body;
  return {
    meta: { ...rest, creator_sub: LOCAL_SUB, boards: boards.map(({ moves: __, ...meta }) => meta) },
    trees: Object.fromEntries(boards.map((b) => [String(b.board), { nodes: mainNodes(b.moves), analysis: {} }])),
    cursor: { board: null, node: ROOT },
  };
}

/** 操作を適用した結果。 不正な操作は投げる。 */
export function applyLocal(doc: LocalDoc, op: LocalOp): LocalDoc {
  const treeOf = (board: number) => {
    const tree = doc.trees[String(board)];
    if (!tree) throw new Error('unknown board');
    return tree;
  };
  const nodeOf = (board: number, id: string) => {
    const n = treeOf(board).nodes[id];
    if (!n) throw new Error('unknown node');
    return n;
  };
  const withTree = (board: number, tree: BoardTree, cursor = doc.cursor): LocalDoc => ({
    ...doc, trees: { ...doc.trees, [String(board)]: tree }, cursor,
  });
  const withNode = (board: number, node: StudyNode, cursor = doc.cursor) => {
    const tree = treeOf(board);
    return withTree(board, { ...tree, nodes: { ...tree.nodes, [node.id]: node } }, cursor);
  };
  switch (op.t) {
    case 'add': {
      const tree = treeOf(op.board);
      if (op.parent !== ROOT && !tree.nodes[op.parent]) throw new Error('unknown parent');
      const json = JSON.stringify(op.mv);
      const same = Object.values(tree.nodes).find((n) => n.parent === op.parent && JSON.stringify(n.mv) === json);
      const seq = Object.keys(tree.nodes).length;
      const node: StudyNode = same
        ? { ...same, deleted: false }
        : { id: `n${seq}`, parent: op.parent, mv: op.mv, main: false, memo: '', deleted: false, by: '', seq };
      return withNode(op.board, node, { board: op.board, node: node.id });
    }
    case 'memo':
      return withNode(op.board, { ...nodeOf(op.board, op.node), memo: op.text });
    case 'delete': {
      const n = nodeOf(op.board, op.node);
      if (n.main) throw new Error('本譜は削除できません');
      return withNode(op.board, { ...n, deleted: true });
    }
    case 'cursor':
      if (op.board !== null && op.node !== ROOT) nodeOf(op.board, op.node);
      return { ...doc, cursor: { board: op.board, node: op.node } };
    case 'analysis': {
      const tree = treeOf(op.board);
      if (op.node !== ROOT) nodeOf(op.board, op.node);
      if ((tree.analysis[op.node]?.depth ?? -1) >= op.depth) return doc;
      return withTree(op.board, { ...tree, analysis: { ...tree.analysis, [op.node]: { depth: op.depth, data: op.data } } });
    }
  }
}

function storedKeys(): string[] {
  return Object.keys(localStorage).filter((k) => k.startsWith(STORAGE_PREFIX));
}

function storedAt(key: string): number {
  try {
    return (JSON.parse(localStorage.getItem(key) ?? '{}') as { at?: number }).at ?? 0;
  } catch {
    return 0;
  }
}

export function saveLocal(id: string, doc: LocalDoc) {
  const trees = Object.fromEntries(Object.entries(doc.trees).map(([k, t]) => [k, { nodes: t.nodes, analysis: {} }]));
  try {
    localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify({ ...doc, trees, at: Date.now() }));
  } catch {
    // 保存できなくても、 開いている間は検討を続けられる。
  }
}

export function loadLocal(id: string): LocalDoc | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    return raw ? (JSON.parse(raw) as LocalDoc) : null;
  } catch {
    return null;
  }
}

export function newLocalStudy(body: LocalBody): string {
  const doc = buildDoc(body);
  const bytes = crypto.getRandomValues(new Uint8Array(11));
  const id = PREFIX + Array.from(bytes, (b) => ID_ALPHABET[b % ID_ALPHABET.length]).join('');
  saveLocal(id, doc);
  try {
    storedKeys().sort((a, b) => storedAt(b) - storedAt(a)).slice(MAX_STORED).forEach((k) => localStorage.removeItem(k));
  } catch {
    // 古い検討が残るだけ。
  }
  return id;
}
